import React from 'react';
import { FunnelElement } from '../../types/element.types';
import { useFunnelStore } from '../../store/funnelStore';

interface LayoutTabProps {
  element: FunnelElement;
}

/**
 * Layout Tab Component
 * Allows editing element layout properties (width, height, alignment, etc.)
 */
const LayoutTab: React.FC<LayoutTabProps> = ({ element }) => {
  const { updateElementStyles } = useFunnelStore();

  const handleStyleChange = (key: string, value: string) => {
    updateElementStyles(element.id, { [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* Dimensions */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Dimensions</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Width
            </label>
            <input
              type="text"
              value={element.styles.width || ''}
              onChange={(e) => handleStyleChange('width', e.target.value)}
              placeholder="auto, 100%, 300px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Height
            </label>
            <input
              type="text"
              value={element.styles.height || ''}
              onChange={(e) => handleStyleChange('height', e.target.value)}
              placeholder="auto, 100px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Max Width
            </label>
            <input
              type="text"
              value={element.styles.maxWidth || ''}
              onChange={(e) => handleStyleChange('maxWidth', e.target.value)}
              placeholder="100%, 600px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Min Height
            </label>
            <input
              type="text"
              value={element.styles.minHeight || ''}
              onChange={(e) => handleStyleChange('minHeight', e.target.value)}
              placeholder="100px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Display */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Display</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Display Type
            </label>
            <select
              value={element.styles.display || 'block'}
              onChange={(e) => handleStyleChange('display', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="block">Block</option>
              <option value="inline">Inline</option>
              <option value="inline-block">Inline Block</option>
              <option value="flex">Flex</option>
              <option value="inline-flex">Inline Flex</option>
              <option value="grid">Grid</option>
              <option value="none">None</option>
            </select>
          </div>
        </div>
      </div>

      {/* Position */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Position</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Position Type
            </label>
            <select
              value={element.styles.position || 'static'}
              onChange={(e) => handleStyleChange('position', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="static">Static</option>
              <option value="relative">Relative</option>
              <option value="absolute">Absolute</option>
              <option value="fixed">Fixed</option>
              <option value="sticky">Sticky</option>
            </select>
          </div>

          {element.styles.position && element.styles.position !== 'static' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Top
                  </label>
                  <input
                    type="text"
                    value={element.styles.top || ''}
                    onChange={(e) => handleStyleChange('top', e.target.value)}
                    placeholder="0px"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Right
                  </label>
                  <input
                    type="text"
                    value={element.styles.right || ''}
                    onChange={(e) => handleStyleChange('right', e.target.value)}
                    placeholder="0px"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Bottom
                  </label>
                  <input
                    type="text"
                    value={element.styles.bottom || ''}
                    onChange={(e) => handleStyleChange('bottom', e.target.value)}
                    placeholder="0px"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Left
                  </label>
                  <input
                    type="text"
                    value={element.styles.left || ''}
                    onChange={(e) => handleStyleChange('left', e.target.value)}
                    placeholder="0px"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Z-Index
            </label>
            <input
              type="number"
              value={element.styles.zIndex || ''}
              onChange={(e) => handleStyleChange('zIndex', e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Flexbox (if display is flex) */}
      {(element.styles.display === 'flex' || element.styles.display === 'inline-flex') && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Flexbox</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Flex Direction
              </label>
              <select
                value={element.styles.flexDirection || 'row'}
                onChange={(e) => handleStyleChange('flexDirection', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="row">Row</option>
                <option value="column">Column</option>
                <option value="row-reverse">Row Reverse</option>
                <option value="column-reverse">Column Reverse</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Justify Content
              </label>
              <select
                value={element.styles.justifyContent || 'flex-start'}
                onChange={(e) => handleStyleChange('justifyContent', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="flex-start">Flex Start</option>
                <option value="center">Center</option>
                <option value="flex-end">Flex End</option>
                <option value="space-between">Space Between</option>
                <option value="space-around">Space Around</option>
                <option value="space-evenly">Space Evenly</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Align Items
              </label>
              <select
                value={element.styles.alignItems || 'stretch'}
                onChange={(e) => handleStyleChange('alignItems', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="stretch">Stretch</option>
                <option value="flex-start">Flex Start</option>
                <option value="center">Center</option>
                <option value="flex-end">Flex End</option>
                <option value="baseline">Baseline</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Gap
              </label>
              <input
                type="text"
                value={element.styles.gap || ''}
                onChange={(e) => handleStyleChange('gap', e.target.value)}
                placeholder="10px"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutTab;

